import React, { createContext, ReactElement, ReactNode, useContext, useEffect, useMemo, useRef, useState } from 'react'

import { ModalState, ModalStates } from '../ModalContentToggler'

interface ContextState<T> {
  activeState?: string
  activeStateModalData?: ModalState<T>
  prevState?: string
  replaceActiveState: (newState?: string) => void
  setActiveState: (newState?: string) => void
}

const ModalContentTogglerContext = createContext<ContextState<any> | undefined>(undefined)

interface ModalContentTogglerContextProps<K> {
  children?: ReactNode
  initialState?: string
  states: ModalStates<K>
}

export const ModalContentTogglerContextProvider = <T,>({
  children,
  initialState,
  states,
}: ModalContentTogglerContextProps<T>): ReactElement => {
  const [activeState, setActiveStateRaw] = useState<string | undefined>()
  const [prevState, setPrevState] = useState<string | undefined>()
  const hasNavigatedRef = useRef(false)

  const setActiveState = (newState?: string) => {
    if (newState !== activeState) {
      hasNavigatedRef.current = true
      setPrevState(activeState)
      setActiveStateRaw(newState)
    }
  }

  const replaceActiveState = (newState?: string) => {
    hasNavigatedRef.current = true
    setPrevState(undefined)
    setActiveStateRaw(newState)
  }

  const activeStateModalData = useMemo(
    () => states.find((modalState) => modalState.id === activeState) || states[0],
    [activeState, states],
  )

  useEffect(() => {
    // initialState chooses the first screen. After the user navigates, data updates must not move them.
    if (hasNavigatedRef.current) {
      return
    }

    setPrevState(undefined)
    setActiveStateRaw(initialState)
  }, [initialState])

  return (
    <ModalContentTogglerContext.Provider
      value={{ activeState, activeStateModalData, prevState, replaceActiveState, setActiveState }}
    >
      {children}
    </ModalContentTogglerContext.Provider>
  )
}

export const useModalContentToggler = () => {
  const context = useContext(ModalContentTogglerContext)

  if (!context) {
    throw new Error('ModalContentTogglerContextProvider is missing in the module!')
  }

  return context
}
